import { useState, useEffect, useCallback, useRef } from "react";
import {
  impersonate as impersonateRequest,
  exitImpersonation as exitImpersonationRequest,
} from "../services/auth.service";

export interface ImpersonationContext {
  targetOrgId: string;
  targetOrgName: string;
  sessionId: string;
  expiresAt: string;
}

const STORAGE_KEYS = {
  ADMIN_TOKEN: "adminToken",
  CONTEXT: "impersonationContext",
  TOKEN: "token",
} as const;

function getStoredContext(): ImpersonationContext | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONTEXT);
    return raw ? (JSON.parse(raw) as ImpersonationContext) : null;
  } catch {
    return null;
  }
}

export function useImpersonation() {
  const [context, setContext] = useState<ImpersonationContext | null>(
    getStoredContext,
  );
  const [timeRemaining, setTimeRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exitRef = useRef<() => Promise<void>>(async () => {});

  const isImpersonating = context !== null;

  const exitImpersonation = useCallback(async () => {
    try {
      await exitImpersonationRequest();
    } catch {
      // Best-effort server end; always restore local session
    }

    const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
    if (adminToken) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, adminToken);
    }
    localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.CONTEXT);
    setContext(null);

    window.location.href = "/global-admin?tab=manage-orgs";
  }, []);

  exitRef.current = exitImpersonation;

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.CONTEXT) {
        setContext(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (!context) {
      setTimeRemaining(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(context.expiresAt).getTime() - Date.now()) / 1000),
      );
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        void exitRef.current();
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [context]);

  const enterImpersonation = useCallback(async (targetOrgId: string) => {
    const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (currentToken) {
      localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, currentToken);
    }

    const resp = (await impersonateRequest(targetOrgId)) as {
      token: string;
      impersonation: ImpersonationContext;
    };

    localStorage.setItem(STORAGE_KEYS.TOKEN, resp.token);

    const impContext: ImpersonationContext = {
      targetOrgId: resp.impersonation.targetOrgId,
      targetOrgName: resp.impersonation.targetOrgName,
      sessionId: resp.impersonation.sessionId,
      expiresAt: resp.impersonation.expiresAt,
    };
    localStorage.setItem(STORAGE_KEYS.CONTEXT, JSON.stringify(impContext));
    setContext(impContext);

    window.location.href = "/home";
  }, []);

  return {
    isImpersonating,
    context,
    enter: enterImpersonation,
    exit: exitImpersonation,
    timeRemaining,
  };
}
